/**
 * Wizard Final Screen — Priorities Reveal.
 *
 * Shown immediately after a successful wizard submission. This is the
 * dopamine moment: the user sees exactly what tasks FinalKey has
 * lined up for them based on their answers.
 *
 * Items created via the LegalZoom branch get a distinctive CTA so the
 * user can one-tap into the affiliate flow. Non-affiliate items show
 * a "You have this already — add the details" subtitle.
 *
 * "Go to my dashboard" resets the nav stack to the dashboard. From
 * here the user is on the normal app path; the priority tasks live
 * in the existing priority UI on the dashboard.
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { ms } from '../../../utils/responsive';
import { useWizard } from '../WizardContext';
import {
  LEGALZOOM_AFFILIATE_ACTIVE,
  getLegalZoomAffiliateUrl,
  EstateDocType,
} from '../../../constants/affiliateLinks';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

// Mirror of WIZARD_TASK_MAP in backend/src/onboarding/onboarding.service.ts
// so the reveal screen can render a readable title for each task key.
// Grouped by wizard step so display order feels natural.
const DISPLAY_BY_TASK_KEY: Record<string, { group: string; title: string }> = {
  spouse_info:          { group: 'Household',     title: 'Spouse information' },
  children_info:        { group: 'Household',     title: 'Children information' },
  pet_info:             { group: 'Household',     title: 'Pet information' },
  primary_physician:    { group: 'Professionals', title: 'Primary care physician' },
  attorney:             { group: 'Professionals', title: 'Attorney' },
  investment_advisor:   { group: 'Professionals', title: 'Investment advisor' },
  accountant:           { group: 'Professionals', title: 'Accountant' },
  last_will:            { group: 'Documents',     title: 'Last Will & Testament' },
  living_trust:         { group: 'Documents',     title: 'Living Trust' },
  advance_directive:    { group: 'Documents',     title: 'Advance Directive' },
  financial_poa:        { group: 'Documents',     title: 'Power of Attorney' },
};

const DOC_TYPE_FROM_TASK_KEY: Record<string, EstateDocType | undefined> = {
  last_will:         'will',
  living_trust:      'trust',
  advance_directive: 'advance_directive',
  financial_poa:     'poa',
};

/**
 * Maps each wizard-surfaced task to the root-navigator stack and
 * intro-screen name where the user actually captures that data.
 * Used to make Priorities Reveal rows tappable — taps reset the nav
 * to [Dashboard, CategoryStack → IntroScreen] so back out of the
 * intro lands on Dashboard rather than returning to the wizard.
 */
const TASK_NAV_MAP: Record<string, { stack: string; screen: string }> = {
  spouse_info:        { stack: 'Personal',             screen: 'SpouseInfoIntro' },
  children_info:      { stack: 'Personal',             screen: 'ChildrenInfoIntro' },
  pet_info:           { stack: 'Personal',             screen: 'PetInfoIntro' },
  primary_physician:  { stack: 'Medical',              screen: 'PrimaryPhysicianIntro' },
  attorney:           { stack: 'Finance',              screen: 'AttorneyInfoIntro' },
  investment_advisor: { stack: 'Finance',              screen: 'InvestmentAdvisorInfoIntro' },
  accountant:         { stack: 'Finance',              screen: 'AccountantInfoIntro' },
  last_will:          { stack: 'EstatePlanning',       screen: 'WillIntro' },
  living_trust:       { stack: 'EstatePlanning',       screen: 'LivingTrustPreferencesIntro' },
  advance_directive:  { stack: 'HealthcareDirectives', screen: 'AdvanceDirectiveIntro' },
  financial_poa:      { stack: 'EstatePlanning',       screen: 'FinancialPOAIntro' },
};

export const WizardPrioritiesRevealScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { submission } = useWizard();

  React.useEffect(() => {
    analytics.track(AnalyticsEvent.PrioritiesRevealShown, {
      priority_count: submission?.priorityTaskKeys.length ?? 0,
    });
    // Fire a separate event when the wizard auto-started the trial.
    // Phase 6 funnel measurement will use this to see trial-start rate
    // for v1 vs v0 users (v0 users click a button; v1 users get it
    // automatically on wizard completion).
    if (submission?.trialJustStarted) {
      analytics.track(AnalyticsEvent.TrialAutoStartedFromWizard);
    }
  }, [submission]);

  const priorities = submission?.priorityTaskKeys ?? [];
  const legalZoomSet = new Set(submission?.legalZoomDocTypes ?? []);

  const successorSeed = submission?.successorSeed ?? null;

  const goToDashboard = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'Dashboard' }],
      }),
    );
  };

  /**
   * Route into AddSuccessorScreen pre-filled with the firstName + email
   * collected on the wizard's soft-ask step. The user completes the
   * full successor setup (phone, passcode, validation Q&A) there.
   *
   * AddSuccessorScreen lives inside the Profile stack, so we first
   * reset the root nav to [Dashboard, Profile] — Dashboard becomes
   * where the user lands when they eventually back out of the Profile
   * stack, and Profile nests straight into AddSuccessor pre-filled.
   */
  const goToSuccessorSetup = () => {
    if (!successorSeed) return;
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [
          { name: 'Dashboard' },
          {
            name: 'Profile',
            state: {
              index: 0,
              routes: [
                {
                  name: 'AddSuccessor',
                  params: {
                    prefilledData: {
                      firstName: successorSeed.firstName,
                      email: successorSeed.email,
                    },
                  },
                },
              ],
            },
          },
        ],
      }),
    );
  };

  const openLegalZoom = async (taskKey: string) => {
    const docType = DOC_TYPE_FROM_TASK_KEY[taskKey];
    if (!docType) return;
    analytics.track(AnalyticsEvent.LegalZoomAffiliateClicked, { doc_type: docType, from: 'priorities_reveal' });
    const url = getLegalZoomAffiliateUrl(docType);
    try {
      await Linking.openURL(url);
    } catch {
      /* no-op: fallback URL always openable */
    }
  };

  /**
   * Navigate directly into the intro screen for a task.
   * Stack reset ensures back from the intro lands on Dashboard — the
   * user just completed the wizard, they shouldn't be able to rewind
   * to this reveal screen.
   */
  const navigateToTask = (taskKey: string) => {
    const target = TASK_NAV_MAP[taskKey];
    if (!target) return;
    analytics.track(AnalyticsEvent.PrioritiesRevealTaskTapped, { task_key: taskKey });
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [
          { name: 'Dashboard' },
          {
            name: target.stack,
            state: {
              index: 0,
              routes: [{ name: target.screen }],
            },
          },
        ],
      }),
    );
  };

  // Group priorities for a tidy list ordered by wizard step
  const grouped: Record<string, string[]> = {};
  priorities.forEach(key => {
    const display = DISPLAY_BY_TASK_KEY[key];
    if (!display) return;
    if (!grouped[display.group]) grouped[display.group] = [];
    grouped[display.group].push(key);
  });
  const groupOrder = ['Household', 'Professionals', 'Documents'];

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Text style={styles.eyebrow}>You're all set ✨</Text>
        <Text style={styles.title}>
          {priorities.length > 0
            ? 'Here\'s what we lined up for you.'
            : 'Your priorities are ready.'}
        </Text>
        <Text style={styles.subtitle}>
          {priorities.length > 0
            ? 'Based on your answers, these are the things worth capturing first. Tap any of them on your dashboard to start.'
            : 'You can add more from your dashboard any time.'}
        </Text>

        {submission?.trialJustStarted ? (
          <View style={styles.trialPill}>
            <Text style={styles.trialPillText}>
              <Text style={styles.trialPillTextBold}>Your 14-day free trial is now active.</Text>{' '}
              Enjoy full access — upgrade any time from Settings.
            </Text>
          </View>
        ) : null}

        {successorSeed ? (
          <TouchableOpacity
            style={styles.successorCard}
            onPress={goToSuccessorSetup}
            accessibilityLabel={`Finish setting up ${successorSeed.firstName ?? 'your successor'}`}
          >
            <Text style={styles.successorEyebrow}>First thing first</Text>
            <Text style={styles.successorTitle}>
              Finish setting up {successorSeed.firstName ?? 'your successor'}
            </Text>
            <Text style={styles.successorSubtitle}>
              Add their phone, a secret passcode, and a validation question so they
              can unlock your vault when the time comes.
            </Text>
            <View style={styles.successorCta}>
              <Text style={styles.successorCtaText}>Set up now →</Text>
            </View>
          </TouchableOpacity>
        ) : null}

        {priorities.length > 0 ? (
          <Text style={styles.listHeading}>Your priority task list</Text>
        ) : null}

        {groupOrder.map(group => {
          const keys = grouped[group];
          if (!keys || keys.length === 0) return null;
          return (
            <View key={group} style={styles.groupSection}>
              <Text style={styles.groupTitle}>{group}</Text>
              {keys.map(taskKey => {
                const display = DISPLAY_BY_TASK_KEY[taskKey];
                const docType = DOC_TYPE_FROM_TASK_KEY[taskKey];
                const isLegalZoom = !!docType && legalZoomSet.has(docType);
                const navigable = !!TASK_NAV_MAP[taskKey];
                return (
                  <TouchableOpacity
                    key={taskKey}
                    style={styles.item}
                    onPress={navigable ? () => navigateToTask(taskKey) : undefined}
                    disabled={!navigable}
                    accessibilityRole={navigable ? 'button' : 'text'}
                    accessibilityLabel={`${display.title}. ${isLegalZoom ? 'Help me create this via LegalZoom.' : 'Add the details when you are ready.'}`}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.itemTitle}>{display.title}</Text>
                      {isLegalZoom ? (
                        <Text style={styles.itemSubCreate}>
                          Help me create this via LegalZoom
                        </Text>
                      ) : (
                        <Text style={styles.itemSubHave}>
                          Tap to start when you're ready
                        </Text>
                      )}
                    </View>
                    {isLegalZoom && LEGALZOOM_AFFILIATE_ACTIVE ? (
                      <TouchableOpacity
                        style={styles.ctaButton}
                        onPress={() => openLegalZoom(taskKey)}
                      >
                        <Text style={styles.ctaButtonText}>Start</Text>
                      </TouchableOpacity>
                    ) : (
                      navigable ? <Text style={styles.chevron}>›</Text> : null
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}

        {priorities.length === 0 ? (
          <View style={styles.emptyCard}>
            <Text style={styles.emptyText}>
              No priority tasks from the wizard this time — but you can always add any task
              yourself from the All Tasks screen.
            </Text>
          </View>
        ) : null}
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={goToDashboard}
          accessibilityLabel="Go to my dashboard"
        >
          <Text style={styles.primaryButtonText}>Go to my dashboard</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollContent: {
    padding: ms(20),
    paddingBottom: ms(24),
  },
  eyebrow: {
    fontSize: ms(14, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
    marginTop: ms(10),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(6),
    marginBottom: ms(10),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(20),
  },
  trialPill: {
    backgroundColor: '#F0FBFB',
    borderRadius: ms(12),
    padding: ms(12),
    marginBottom: ms(16),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  trialPillText: {
    fontSize: ms(13, 0.3),
    color: '#0F4F50',
    lineHeight: ms(19, 0.3),
  },
  trialPillTextBold: {
    fontWeight: '700',
  },
  successorCard: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(16),
    padding: ms(18),
    marginBottom: ms(22),
  },
  successorEyebrow: {
    fontSize: ms(11, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: ms(6),
  },
  successorTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  successorSubtitle: {
    fontSize: ms(14, 0.3),
    color: '#C6D2E3',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(14),
  },
  successorCta: {
    alignSelf: 'flex-start',
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(16),
    paddingVertical: ms(10),
    borderRadius: ms(20),
  },
  successorCtaText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: ms(13, 0.3),
  },
  listHeading: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginTop: ms(4),
    marginBottom: ms(14),
  },
  groupSection: {
    marginBottom: ms(18),
  },
  groupTitle: {
    fontSize: ms(13, 0.3),
    fontWeight: '700',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: ms(8),
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: ms(14),
    borderRadius: ms(12),
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
    marginBottom: ms(10),
  },
  itemTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  itemSubHave: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  itemSubCreate: {
    fontSize: ms(12, 0.3),
    color: '#29BFC0',
    marginTop: ms(2),
  },
  ctaButton: {
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(16),
    paddingVertical: ms(10),
    borderRadius: ms(20),
    marginLeft: ms(10),
  },
  chevron: {
    fontSize: ms(24, 0.3),
    color: '#9CA3AF',
    marginLeft: ms(10),
    fontWeight: '300',
  },
  ctaButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: ms(13, 0.3),
  },
  emptyCard: {
    backgroundColor: '#FFFFFF',
    padding: ms(18),
    borderRadius: ms(12),
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
  },
  emptyText: {
    fontSize: ms(14, 0.3),
    color: '#4B5563',
    lineHeight: ms(20, 0.3),
  },
  footer: {
    paddingHorizontal: ms(20),
    paddingVertical: ms(10),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(28),
    paddingVertical: ms(16),
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
});
